import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useMemo } from 'react';

import { SocketContext } from '../providers/SocketProvider/SocketProvider';
import BackendService from '../services/backendService';
import useAiAssessedScores from './useAiAssessedScores';

const useListAllAiAssessedScores = ({
  gameId,
  tenantId,
  slideId,
  isUsingSockets,
  setShowMediaIsDisabled,
}: {
  gameId: string;
  tenantId: string;
  slideId?: string;
  isUsingSockets: boolean;
  setShowMediaIsDisabled?: (val: boolean) => void;
}) => {
  const { gameAPI } = useContext(SocketContext);

  const scoresCacheKey = useMemo(() => ['aiAssessedScores', gameId, slideId], [
    gameId,
    slideId,
  ]);
  const {
    data,
    isFetching: isFetchingAiAssessedScores,
    refetch: refetchAiAssessedScores,
  } = useQuery({
    queryKey: scoresCacheKey,
    queryFn: async () => {
      if (isUsingSockets) {
        if (!gameAPI || !slideId) return Promise.resolve(undefined);
        const results = await gameAPI.game.listAllAiAssessments({
          gameId,
          slideId,
          tenantId,
        });
        return results.results;
      } else {
        //if if is not during the live game they are viewing the results - don't use sockets
        if (!slideId) return Promise.resolve(undefined);
        const res = await BackendService.instance.game.retrievePublicAiFeedback({
          gameId,
          slideId,
        });
        if (!res) return Promise.resolve(undefined);
        return res.results;
      }
    },
    enabled:
      Boolean(isUsingSockets && gameAPI && slideId) ||
      Boolean(!isUsingSockets && slideId),
    staleTime: isUsingSockets ? 0 : 1000 * 60 * 5, // No staleTime when using sockets to ensure real-time updates
    retry: false,
    refetchOnMount: isUsingSockets, // Refetch when component mounts if using sockets
  });

  const {
    generateAllAiAssessedScores,
    isFetchingAiAssessedScores: isGeneratingNewScores,
    aiAssessedScores: newAiAssessedScores,
  } = useAiAssessedScores({
    gameId,
    slideId: slideId || '',
    tenantId,
    setShowMediaIsDisabled,
  });

  return {
    aiAssessedScores: data || newAiAssessedScores,
    isGeneratingNewScores,
    isFetchingAiAssessedScores,
    refetchAiAssessedScores,
  };
};

export default useListAllAiAssessedScores;
